import { useEffect, useState, useMemo } from 'react';
import debounce from 'lodash/debounce';
import { Button, Input, VStack, Text } from '@chakra-ui/react';
import { useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toaster } from '@/components/ui/toaster';
import { Field } from '@/components/ui/field';
import {
  DialogBackdrop,
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { NativeSelectField, NativeSelectRoot } from '@/components/ui/native-select';
import api from '@/utils/api';
import { roleOptions, DailogProps } from '@/lib/types';

interface Props extends DailogProps {
  onSuccess(): void;
}

enum UserStatus {
  UNKNOWN = 'unknown',
  EXISTING = 'existing',
  NEW = 'new',
  CHECKING = 'checking',
}

export default function InviteUserModal({ isOpen, onClose, onSuccess }: Props) {
  const { workspaceId } = useParams();
  const [userStatus, setUserStatus] = useState<UserStatus>(UserStatus.UNKNOWN);

  const formSchema = useMemo(() => {
    return z.object({
      email: z.string().min(1, { message: 'Email is required' }).email('Invalid email address'),
      roleId: z.string().min(1, { message: 'Role is required' }),
      firstName: z
        .string()
        .refine((data) => userStatus === UserStatus.EXISTING || data.length > 0, {
          message: 'First name is required',
        }),
      lastName: z.string().refine((data) => userStatus === UserStatus.EXISTING || data.length > 0, {
        message: 'Last name is required',
      }),
    });
  }, [userStatus]);

  type FormValues = z.infer<typeof formSchema>;

  const {
    register,
    reset,
    handleSubmit,
    trigger,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: '',
      roleId: '',
      firstName: '',
      lastName: '',
    },
  });

  const debouncedCheckUser = useMemo(
    () =>
      debounce(async (email: string) => {
        if (!email || !email.includes('@')) return;

        setUserStatus(UserStatus.CHECKING);

        try {
          const response = await api.get(`invite/check/${email}`);
          if (response?.data?.resource) {
            setUserStatus(UserStatus.EXISTING);
          }
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
        } catch (err: any) {
          setUserStatus(UserStatus.NEW);
        }
      }, 500),
    [],
  );

  const email = watch('email');

  useEffect(() => {
    if (email && email.includes('@')) {
      debouncedCheckUser(email);
    } else {
      setUserStatus(UserStatus.UNKNOWN);
    }
    return () => {
      debouncedCheckUser.cancel();
    };
  }, [email, debouncedCheckUser]);

  useEffect(() => {
    if (userStatus !== UserStatus.UNKNOWN && userStatus !== UserStatus.CHECKING) {
      trigger(['firstName', 'lastName']);
    }
  }, [userStatus, trigger]);

  useEffect(() => {
    if (isOpen) {
      reset();
      setUserStatus(UserStatus.UNKNOWN);
    }
  }, [isOpen, reset]);

  const onSubmit = async (data: FormValues) => {
    try {
      if (userStatus === UserStatus.EXISTING) {
        await api.post(`users/invite`, {
          email: data.email,
          workspace_id: parseInt(workspaceId ?? ''),
          role_id: parseInt(data.roleId, 10),
        });
      } else {
        await api.post(`users/invite/new`, {
          email: data.email,
          first_name: data.firstName,
          last_name: data.lastName,
          workspace_id: parseInt(workspaceId ?? ''),
          role_id: parseInt(data.roleId, 10),
        });
      }

      toaster.create({
        type: 'success',
        title: 'Invitation email sent.',
      });
      onClose();
      onSuccess();
    } catch (err: any) {
      toaster.create({
        type: 'error',
        title:
          err.response?.data?.error ||
          'Error: The backend API is not responding. Please try again later.',
      });
    }
  };

  const isExistingUser = userStatus === UserStatus.EXISTING;
  const isNewUser = userStatus === UserStatus.NEW;
  const isCheckingUser = userStatus === UserStatus.CHECKING;
  const showRoleField = userStatus !== UserStatus.UNKNOWN && userStatus !== UserStatus.CHECKING;

  return (
    <DialogRoot lazyMount open={isOpen} onOpenChange={onClose}>
      <DialogBackdrop />
      <DialogTrigger />
      <DialogContent>
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogCloseTrigger />
          <DialogHeader>
            <DialogTitle>Invite User</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <VStack align={'stretch'} gap={4}>
              <Field label='Email' invalid={!!errors.email} errorText={errors.email?.message}>
                <Input {...register('email')} type='email' autoComplete='email' />
              </Field>

              {isCheckingUser && (
                <Text fontSize='sm' color='gray.500'>
                  Checking email...
                </Text>
              )}

              {isNewUser && (
                <>
                  <Field
                    label='First name'
                    invalid={!!errors.firstName}
                    errorText={errors.firstName?.message}
                  >
                    <Input {...register('firstName')} autoComplete='given-name' />
                  </Field>
                  <Field
                    label='Last name'
                    invalid={!!errors.lastName}
                    errorText={errors.lastName?.message}
                  >
                    <Input {...register('lastName')} autoComplete='family-name' />
                  </Field>
                </>
              )}

              {isExistingUser && (
                <Text fontSize='sm' color='blue.500'>
                  This user already exists. They will be invited to join this workspace.
                </Text>
              )}

              {showRoleField && (
                <Field label='Role' invalid={!!errors.roleId} errorText={errors.roleId?.message}>
                  <NativeSelectRoot>
                    <NativeSelectField placeholder={'Select role'} {...register('roleId')}>
                      {roleOptions.map((role) => (
                        <option key={role.value} value={role.value}>
                          {role.label}
                        </option>
                      ))}
                    </NativeSelectField>
                  </NativeSelectRoot>
                </Field>
              )}
            </VStack>
          </DialogBody>
          <DialogFooter>
            <Button colorPalette='brand' variant={'outline'} onClick={onClose}>
              Cancel
            </Button>
            <Button
              colorPalette='brand'
              type='submit'
              loading={isSubmitting || isCheckingUser}
              loadingText='Loading...'
              disabled={userStatus === UserStatus.UNKNOWN || isCheckingUser}
            >
              Submit
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </DialogRoot>
  );
}
